import {useCallback} from 'react';
import mixpanel from 'mixpanel-browser';
import {REACT_APP_MIXPANEL_TOKEN} from '../env';

/**
 * Custom hook for Mixpanel tracking with error handling
 * Wraps all Mixpanel methods in try-catch blocks to prevent errors from breaking the app
 */
const useMixpanel = () => {
  // Initialize Mixpanel if not already initialized
  try {
    if (REACT_APP_MIXPANEL_TOKEN && !mixpanel.__loaded) {
      mixpanel.init(REACT_APP_MIXPANEL_TOKEN, {
        debug: process.env.NODE_ENV !== 'production',
        track_pageview: true,
        persistence: 'localStorage'
      });
    }
  } catch (error) {
    console.error('Error initializing Mixpanel:', error);
  }

  /**
   * Track an event in Mixpanel
   * @param {string} eventName - Name of the event to track
   * @param {Object} [properties={}] - Properties to send with the event
   */
  const track = useCallback((eventName, properties = {}) => {
    try {
      mixpanel.track(eventName, properties);
    } catch (error) {
      console.error(`Error tracking event "${eventName}":`, error);
    }
  }, []);

  /**
   * Identify a user in Mixpanel
   * @param {string} userId - Unique identifier for the user
   * @param {Object} [traits={}] - User properties to set
   */
  const identify = useCallback((userId, traits = {}) => {
    try {
      mixpanel.identify(userId);
      if (Object.keys(traits)?.length > 0) {
        mixpanel.people.set(traits);
      }
    } catch (error) {
      console.error(`Error identifying user "${userId}":`, error);
    }
  }, []);

  /**
   * Register super properties that will be sent with all events
   * @param {Object} properties - Properties to register
   * @param {boolean} [once=false] - If true, only register if not already set
   */
  const register = useCallback((properties, once = false) => {
    try {
      if (once) {
        mixpanel.register_once(properties);
      } else {
        mixpanel.register(properties);
      }
    } catch (error) {
      console.error('Error registering properties:', error);
    }
  }, []);

  /**
   * Reset the user's identity and clear their data
   */
  const reset = useCallback(() => {
    try {
      mixpanel.reset();
    } catch (error) {
      console.error('Error resetting Mixpanel user:', error);
    }
  }, []);

  /**
   * Track a page view
   * @param {string} page - Name of the page being viewed
   * @param {Object} [properties={}] - Additional properties
   */
  const trackPageView = useCallback((page, properties = {}) => {
    try {
      mixpanel.track('Page Viewed', {
        page,
        ...properties
      });
    } catch (error) {
      console.error(`Error tracking page view for "${page}":`, error);
    }
  }, []);

  return {
    track,
    identify,
    register,
    reset,
    trackPageView
  };
};

export default useMixpanel;
